import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Button,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { format, parseISO, isBefore, startOfDay, addDays } from "date-fns";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { get, liftDisconnectHold } from "../api/actions";
import MUITable from "../../../components/MUITable";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileUploadIcon from '@mui/icons-material/FileUpload';
function DisconnectHoldTable(props) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState({
    expiresAfter: '',
    expiresBefore: '',
    holdType: '',
    isActive: 'true',
    search: '',
    page: 1,
    limit: 10,
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [holdTypeFilter, setHoldTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState('true'); // Default to Active

  const total = props.disconnectHolds?.data?.disconnectHoldsList?.total || 0;
  const currentPage = props.disconnectHolds?.data?.disconnectHoldsList?.page || 1;
  const currentLimit = props.disconnectHolds?.data?.disconnectHoldsList?.limit || 10;

  useEffect(() => {
    props.get(searchParams);
  }, [searchParams]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    const dateObj = moment.isMoment(date) ? date.toDate() : date;
    const formattedDate = dateObj ? format(dateObj, 'yyyy-MM-dd') : '';
    const newParams = {
      ...searchParams,
      expiresAfter: formattedDate,
      page: 1, // Reset to first page when filter changes
    };
    setSearchParams(newParams);
    props.get(newParams);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    const dateObj = moment.isMoment(date) ? date.toDate() : date;
    const formattedDate = dateObj ? format(dateObj, 'yyyy-MM-dd') : '';
    const newParams = {
      ...searchParams,
      expiresBefore: formattedDate,
      page: 1, // Reset to first page when filter changes
    };
    setSearchParams(newParams);
    props.get(newParams);
  };

  const handleClearDates = () => {
    setStartDate(null);
    setEndDate(null);
    const newParams = {
      ...searchParams,
      expiresAfter: '',
      expiresBefore: '',
      page: 1, // Reset to first page when filter changes
    };
    setSearchParams(newParams);
    props.get(newParams);
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    const newParams = {
      ...searchParams,
      search: query,
      page: 1, // Reset to first page when search changes
    };
    setSearchParams(newParams);
    props.get(newParams);
  };

  const handleGroupChipClick = (hold) => {
    const groupId = hold.groupId || hold.group?.id || hold.group?.uuid;
    if (groupId) {
      navigate(`/disconnect-management?groupId=${groupId}`);
    }
  };

  const handleAction = (actionKey, hold) => {
    switch (actionKey) {
      case 'edit':
        if (props.onEditHold) {
          props.onEditHold(hold);
        }
        break;
      case 'delete':
        if (props.onDeleteHold) {
          props.onDeleteHold(hold);
        }
        break;
      case 'lift':
        if (props.onLiftHold) {
          props.onLiftHold(hold);
        }
        break;
      default:
        break;
    }
  };

  const handleHoldTypeFilter = (type) => {
    setHoldTypeFilter(type);
    const newParams = {
      ...searchParams,
      holdType: type || '',
      page: 1, // Reset to first page when filter changes
    };
    setSearchParams(newParams);
    props.get(newParams);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    const newParams = {
      ...searchParams,
      isActive: status || '',
      page: 1, // Reset to first page when filter changes
    };
    setSearchParams(newParams);
    props.get(newParams);
  };

  const handleChangePage = (event, newPage) => {
    const newParams = {
      ...searchParams,
      page: newPage + 1, // MUI TablePagination uses 0-based index, backend uses 1-based
    };
    setSearchParams(newParams);
    props.get(newParams);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    const newParams = {
      ...searchParams,
      limit: newLimit,
      page: 1, // Reset to first page when changing rows per page
    };
    setSearchParams(newParams);
    props.get(newParams);
  };

  const renderExpandedContent = (hold) => {
    const group = hold.group || {};
    const property = hold.property || {};
    const properties = group.properties || group.propertyList || group.property_list || group.items || [];

    return (
      <Table size="small" aria-label="hold-details">
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Target</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell component="th" scope="row">
              <Chip
                label={hold.holdType}
                color={hold.holdType === 'GROUP' ? 'primary' : hold.holdType === 'PROPERTY' ? 'secondary' : 'default'}
                size="small"
              />
            </TableCell>
            <TableCell>
              {hold.holdType === 'GROUP' && group.name ? (
                <Chip
                  label={group.name}
                  style={{
                    backgroundColor: group.bgColorTag ? `#${group.bgColorTag}` : 'transparent',
                    color: group.bgColorTag ? '#ffffff' : '#000000',
                    border: group.bgColorTag ? 'none' : '1px solid #ccc',
                  }}
                  variant={group.bgColorTag ? "filled" : "outlined"}
                  size="small"
                />
              ) : hold.holdType === 'PROPERTY' && property.name ? (
                property.name
              ) : (
                'Unknown'
              )}
            </TableCell>
            <TableCell>{hold.reason}</TableCell>
            <TableCell>{hold.notes || 'No notes'}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  const columns = [
    {
      field: 'holdType',
      label: 'Hold Type',
      type: 'chip',
      render: (hold) => (
        <Chip
          label={hold.holdType}
          color={hold.holdType === 'GROUP' ? 'primary' : hold.holdType === 'PROPERTY' ? 'secondary' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'target',
      label: 'Target',
      type: 'chip',
      render: (hold) => {
        if (hold.holdType === 'GROUP') {
          return (
            <Chip
              label={hold?.groupName || 'Unknown Group'}
              onClick={(e) => {
                e.stopPropagation();
                handleGroupChipClick(hold);
              }}
              style={{
                backgroundColor: hold?.bgColorTag ? `#${hold.bgColorTag}` : 'transparent',
                color: hold?.bgColorTag ? '#ffffff' : '#000000',
                border: hold?.bgColorTag ? 'none' : '1px solid #ccc',
                cursor: 'pointer'
              }}
              variant={hold?.bgColorTag ? "filled" : "outlined"}
              size="small"
            />
          );
        } else if (hold.holdType === 'PROPERTY') {
          return (
            <Chip
              label={hold?.propertyName || 'Unknown Property'}
              color="secondary"
              size="small"
            />
          );
        } else if (hold.holdType === 'ACCOUNT') {
          return (
            <Chip
              label={hold?.accountNum || 'Unknown Account'}
              color="primary"
              size="small"
            />
          );
        } else {
          return (
            <Chip
              label="Default"
              color="default"
              size="small"
            />
          );
        }
      }
    },
    {
      field: 'reason',
      label: 'Reason',
      type: 'text',
      render: (hold) => {
        return hold.reason || 'No reason provided';
      }
    },
    {
      field: 'liftReason',
      label: 'Lift Reason',
      type: 'text',
      render: (hold) => {
        return hold.liftReason || (hold.isActive ? '-' : 'N/A');
      }
    },
    {
      field: 'expiresAt',
      label: 'Expires',
      type: 'date',
      render: (hold) => {
        const date = hold.expiresAt;
        if (!date) return 'N/A';
        
        const dateStr = typeof date === 'string' && date.includes('T') ? date.split('T')[0] : date;
        const dateObj = parseISO(dateStr);
        const today = startOfDay(new Date());
        const sevenDaysFromNow = startOfDay(addDays(new Date(), 7));
        const isExpired = isBefore(dateObj, today);
        const isExpiringSoon = isBefore(dateObj, sevenDaysFromNow) && !isExpired;
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="body2" 
              color={isExpired ? 'error' : isExpiringSoon ? 'warning.main' : 'text.primary'}
            >
              {format(dateObj, "MM/dd/yyyy")}
            </Typography>
            {isExpired && (
              <Chip label="Expired" color="error" size="small" />
            )}
            {isExpiringSoon && !isExpired && (
              <Chip label="Expires Soon" color="warning" size="small" />
            )}
          </Box>
        );
      }
    },
    {
      field: 'isActive',
      label: 'Status',
      type: 'status',
      render: (hold) => (
        <Chip
          label={hold.isActive ? "Active" : "Inactive"}
          color={hold.isActive ? "success" : "default"}
          size="small"
        />
      )
    },
    {
      field: 'created',
      label: 'Created',
      type: 'date',
      render: (hold) => {
        const date = hold.created || hold.createdAt;
        return date ? format(parseISO(date), "MM/dd/yyyy") : 'N/A';
      }
    }
  ];


  const actions = [
    {
      key: 'lift',
      icon: FileUploadIcon,
      tooltip: 'Lift hold',
      color: 'success',
      disabled: (hold) => !hold.isActive
    },
    {
      key: 'edit',
      icon: EditIcon,
      tooltip: 'Edit hold',
      color: 'primary',
      disabled: (hold) => !hold.isActive
    },
    {
      key: 'delete',
      icon: DeleteIcon,
      tooltip: 'Delete hold',
      color: 'error'
    }
  ];

  const data = props.disconnectHolds?.data?.disconnectHoldsList?.items || [];
  const isLoading = props.disconnectHolds?.isLoading || false;
  const error = props.disconnectHolds?.error;

  if (error) {
    return (
      <Box p={3} textAlign="center">
        <Typography color="error">
          Error loading disconnect holds: {error.message}
        </Typography>
      </Box>
    );
  }

  const getHoldTypeChipStyle = (type) => {
    if (holdTypeFilter === type) {
      switch (type) {
        case "GROUP":
          return {
            backgroundColor: "rgba(33, 150, 243, 0.1)",
            color: "#2196f3 !important",
            border: "1px solid #2196f3",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(33, 150, 243, 0.2)",
              border: "1px solid #2196f3",
            }
          };
        case "PROPERTY":
          return {
            backgroundColor: "rgba(156, 39, 176, 0.1)",
            color: "#9c27b0 !important",
            border: "1px solid #9c27b0",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(156, 39, 176, 0.2)",
              border: "1px solid #9c27b0",
            }
          };
        case "ACCOUNT":
          return {
            backgroundColor: "rgba(255, 152, 0, 0.1)",
            color: "#ff9800 !important",
            border: "1px solid #ff9800",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(255, 152, 0, 0.2)",
              border: "1px solid #ff9800",
            }
          };
        case null:
          return {
            backgroundColor: "rgba(76, 175, 80, 0.1)",
            color: "#4caf50 !important",
            border: "1px solid #4caf50",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(76, 175, 80, 0.2)",
              border: "1px solid #4caf50",
            }
          };
        default:
          return {
            backgroundColor: "rgba(158, 158, 158, 0.1)",
            color: "#757575 !important",
            border: "1px solid #bdbdbd",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(158, 158, 158, 0.2)",
              border: "1px solid #bdbdbd",
            }
          };
      }
    }
    return {
      backgroundColor: "rgba(158, 158, 158, 0.1)",
      color: "#757575 !important",
      border: "1px solid #bdbdbd",
      fontWeight: 500,
      borderRadius: "16px",
      fontSize: "0.875rem",
      textTransform: "none",
      "&:hover": {
        backgroundColor: "rgba(158, 158, 158, 0.2)",
        border: "1px solid #bdbdbd",
      }
    };
  };

  const getStatusChipStyle = (status) => {
    if (statusFilter === status) {
      switch (status) {
        case "true":
          return {
            backgroundColor: "rgba(76, 175, 80, 0.1)",
            color: "#4caf50 !important",
            border: "1px solid #4caf50",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(76, 175, 80, 0.2)",
              border: "1px solid #4caf50",
            }
          };
        case "false":
          return {
            backgroundColor: "rgba(255, 193, 7, 0.1)",
            color: "#ff9800 !important",
            border: "1px solid #ff9800",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(255, 193, 7, 0.2)",
              border: "1px solid #ff9800",
            }
          };
        case null:
          return {
            backgroundColor: "rgba(76, 175, 80, 0.1)",
            color: "#4caf50 !important",
            border: "1px solid #4caf50",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(76, 175, 80, 0.2)",
              border: "1px solid #4caf50",
            }
          };
        default:
          return {
            backgroundColor: "rgba(158, 158, 158, 0.1)",
            color: "#757575 !important",
            border: "1px solid #bdbdbd",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(158, 158, 158, 0.2)",
              border: "1px solid #bdbdbd",
            }
          };
      }
    }
    return {
      backgroundColor: "rgba(158, 158, 158, 0.1)",
      color: "#757575 !important",
      border: "1px solid #bdbdbd",
      fontWeight: 500,
      borderRadius: "16px",
      fontSize: "0.875rem",
      textTransform: "none",
      "&:hover": {
        backgroundColor: "rgba(158, 158, 158, 0.2)",
        border: "1px solid #bdbdbd",
      }
    };
  };

  const HoldTypeFilters = () => {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          onClick={() => handleHoldTypeFilter(null)}
          sx={getHoldTypeChipStyle(null)}
          variant="outlined"
          size="small"
        >
          All Types
        </Button>
        <Button
          onClick={() => handleHoldTypeFilter('GROUP')}
          sx={getHoldTypeChipStyle('GROUP')}
          variant="outlined"
          size="small"
        >
          Group
        </Button>
        <Button
          onClick={() => handleHoldTypeFilter('PROPERTY')}
          sx={getHoldTypeChipStyle('PROPERTY')}
          variant="outlined"
          size="small"
        >
          Property
        </Button>
        <Button
          onClick={() => handleHoldTypeFilter('ACCOUNT')}
          sx={getHoldTypeChipStyle('ACCOUNT')}
          variant="outlined"
          size="small"
        >
          Account
        </Button>
      </Box>
    );
  };

  const StatusFilters = () => {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          onClick={() => handleStatusFilter(null)}
          sx={getStatusChipStyle(null)}
          variant="outlined"
          size="small"
        >
          All Status
        </Button>
        <Button
          onClick={() => handleStatusFilter('true')}
          sx={getStatusChipStyle('true')}
          variant="outlined"
          size="small"
        >
          Active
        </Button>
        <Button
          onClick={() => handleStatusFilter('false')}
          sx={getStatusChipStyle('false')}
          variant="outlined"
          size="small"
        >
          Inactive
        </Button>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by reason, notes, or lift reason..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ mb: 2 }}
          />
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>
            Filter by Expiration Date
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DatePicker
              label="From Date"
              value={startDate}
              onChange={handleStartDateChange}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 200 }
                }
              }}
            />
            <DatePicker
              label="To Date"
              value={endDate}
              onChange={handleEndDateChange}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 200 }
                }
              }}
            />
            <Button 
              variant="outlined" 
              size="small"
              onClick={handleClearDates}
              disabled={!startDate && !endDate}
            >
              Clear Dates
            </Button>
          </Box>
        </Box>
        
        {/* Custom Filters section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>
                Hold Type
              </Typography>
              <HoldTypeFilters />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>
                Status
              </Typography>
              <StatusFilters />
            </Box>
          </Box>
        </Box>
      </Box>
      
      <MUITable
        data={data}
        columns={columns}
        searchConfig={{
          enabled: false
        }}
        expandableConfig={{
          enabled: true,
          renderExpandedContent,
          expandTriggerField: 'holdType'
        }}
        actionsConfig={{
          enabled: true,
          actions
        }}
        loadingState={{
          isLoading,
          loadingText: "Loading disconnect holds..."
        }}
        emptyState={{
          message: "No disconnect holds found. Try adjusting your filters.",
          showWhenEmpty: true
        }}
        paginationConfig={{
          enabled: true,
          total: total,
          page: currentPage - 1, // Convert to 0-based index for MUI
          rowsPerPage: currentLimit,
          rowsPerPageOptions: [10, 25, 50, 100],
          onPageChange: handleChangePage,
          onRowsPerPageChange: handleChangeRowsPerPage,
          labelDisplayedRows: ({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
        }}
        onAction={handleAction}
      />
    </LocalizationProvider>
  );
}

DisconnectHoldTable.propTypes = {
  disconnectHolds: PropTypes.object.isRequired,
  get: PropTypes.func.isRequired,
  onEditHold: PropTypes.func,
  onDeleteHold: PropTypes.func,
  onLiftHold: PropTypes.func,
};

function State(state) {
  return {
    disconnectHolds: state.disconnectHolds,
  };
}

export default connect(State, { get, liftDisconnectHold })(DisconnectHoldTable);
